import { useCallback, useState } from 'react'
import { Linking, Platform } from 'react-native'
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../constants/api'
import { useDialog } from '../../shared/hooks/useDialog'
import { useLanguage } from '../../i18n/useLanguage'

interface ApiEnvelope<T> {
  success?: boolean
  data?: T
  error?: string
}

function redirectToExternalUrl(url: string): void {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.location.assign(url)
    return
  }
  void Linking.openURL(url)
}

/**
 * Org admin Stripe actions (B2B checkout + customer portal). Web uses full-page redirect to Stripe.
 * Returns `stripeConfigured` so the UI can disable buttons preemptively.
 */
export function useB2bBillingActions(accountId: string | null, account?: { stripeBillingEnabled?: boolean }) {
  const apiClient = useApiClient()
  const { showAlert } = useDialog()
  const { language } = useLanguage()
  const [busy, setBusy] = useState(false)

  const stripeConfigured = account?.stripeBillingEnabled !== false

  const startCheckout = useCallback(
    async (targetPlanTier: 'BUSINESS' | 'ENTERPRISE', bucketKey?: string) => {
      if (!accountId) return
      setBusy(true)
      try {
        const res = await apiClient<ApiEnvelope<{ url: string }>>(
          API_ENDPOINTS.billing.b2bCheckoutSession(accountId),
          {
            method: 'POST',
            data: {
              targetPlanTier,
              ...(bucketKey ? { bucketKey } : {}),
            },
          },
        )
        const envelope = res.data
        const url = envelope?.data?.url
        if (!envelope?.success || !url) {
          await showAlert({
            title: 'Billing',
            message: extractErrorMessage(envelope, 'Could not start checkout.'),
            type: 'danger',
          })
          return
        }
        redirectToExternalUrl(url)
      } catch (e) {
        await showAlert({
          title: 'Billing',
          message: extractErrorMessage(e, 'Could not start checkout.'),
          type: 'danger',
        })
      } finally {
        setBusy(false)
      }
    },
    [accountId, apiClient, showAlert],
  )

  const openPortal = useCallback(async () => {
    if (!accountId) return
    setBusy(true)
    try {
      const res = await apiClient<ApiEnvelope<{ url: string }>>(
        API_ENDPOINTS.billing.b2bPortalSession(accountId),
        { method: 'POST', data: { locale: language } },
      )
      const envelope = res.data
      const url = envelope?.data?.url
      if (!envelope?.success || !url) {
        await showAlert({
          title: 'Billing',
          message: extractErrorMessage(envelope, 'Could not open billing portal.'),
          type: 'danger',
        })
        return
      }
      redirectToExternalUrl(url)
    } catch (e) {
      await showAlert({
        title: 'Billing',
        message: extractErrorMessage(e, 'Could not open billing portal.'),
        type: 'danger',
      })
    } finally {
      setBusy(false)
    }
  }, [accountId, apiClient, language, showAlert])

  return { startCheckout, openPortal, busy, stripeConfigured }
}
